import React from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import Button from '../ui/Button';
import { useAuth } from '../contexts/AuthContext';
import { usePermissions } from '../hooks/usePermissions';
import logger from '../utils/logger';
import ViewAsBanner from './ViewAsBanner';

export default function ProtectedRoute({ children, requiredPermission }) {
    const { currentUser } = useAuth();
    const { hasPermission, viewingAs } = usePermissions();
    const location = useLocation();
    const navigate = useNavigate();

    if (!currentUser) {
        return <Navigate to="/login" />;
    }

    // 🔒 Security Policy: Force Password Change
    if (currentUser?.security?.forceChangePassword) {
        if (location.pathname !== '/change-password') {
            return <Navigate to="/change-password" replace />;
        }
    }

    // Check Permission Synchronously
    if (!requiredPermission && process.env.NODE_ENV === 'development') {
        logger.warn("⚠️ ProtectedRoute used without 'requiredPermission'. Route is accessible to ALL logged-in users.");
    }

    // Strict Check
    const hasAccess = requiredPermission ? hasPermission(requiredPermission) : true;

    if (requiredPermission && !hasAccess) {
        return (
            <div className="min-h-screen flex flex-col items-center justify-center gap-4 p-4">
                {/* while an admin looks through a role: the banner and its Exit here too, or the tab stays stuck on this
                    screen (review 3 Oct 2026) */}
                <div className="w-full max-w-2xl"><ViewAsBanner /></div>
                <div className="bg-white p-8 text-center max-w-lg border-t-8 border-red-500 shadow-xl rounded-lg">
                    <div className="text-6xl mb-4">🚫</div>
                    <h1 className="text-2xl font-bold mb-2 text-gray-800">Access Denied</h1>
                    <p className="mb-6 text-gray-600">You do not have permission to view the <span className="font-semibold text-red-600">{requiredPermission}</span> module.</p>
                    <p className="text-sm text-gray-500 mb-6">{viewingAs
                        ? `The role "${viewingAs.name}" cannot open this page.`
                        : 'Contact your administrator to request access.'}</p>
                    {/* an old link or bookmark (e.g. /dashboard) for a role without that page: its own first page */}
                    <Button variant="primary" size="sm" onClick={() => navigate('/')}>Go to my pages</Button>
                </div>
            </div>
        );
    }

    return children;
}
